import { Col, Divider, Row, Space, Spin, Table, Tag, Typography, Modal, Rate, Badge } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import Link from 'antd/lib/typography/Link';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParam } from 'use-query-params';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { UserAvatar } from '../../../../components/atoms/UserAvatar';
import ReviewPreview from '../../../../components/molecules/ReviewPreview/ReviewPreview';
import { useConnection } from '../../../../connection/Application';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import {
  AlignLeftOutlined,
  ArrowRightOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  RetweetOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import ReactHtmlParser from '../../../../utils/SafeHtmlParser';
const { Title, Text } = Typography;

const regex = /(<([^>]+)>)/gi;

const getObjectionStatusMeta = (status?: string) => {
  const normalizedStatus = status?.toLowerCase();

  if (!normalizedStatus) {
    return null;
  }

  return {
    color:
      normalizedStatus === 'accepted'
        ? 'green'
        : normalizedStatus === 'rejected'
        ? 'red'
        : 'orange',
    label: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1),
  };
};

const UserInternalCustomerAnswers: FC = () => {
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showContent, setShowContent] = useState<boolean>(true);
  const [tableData, setTableData] = useState();
  const connection = useConnection();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [record, setRecord] = useState<any>();
  const history1 = useHistory();
  const params = useParams<{ id: string; name: string }>();

  const columns = useMemo(() => {
    return [
      {
        title: 'Team Name',
        dataIndex: 'teamName',
        key: 'teamName',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            <UserAvatar type={record.itemType} src={record.image} />
            {record.status !== 'not submitted' && record.status !== 'drafted' ? (
              <Link
                onClick={() => {
                  setRecord(record);
                  setModalVisible(true);
                }}>
                {`${record.reviewer.name} `}
                <ArrowRightOutlined />
                {` ${record.team.name}`}
              </Link>
            ) : (
              <>
                {`${record?.reviewer?.name ?? ''} `}
                <ArrowRightOutlined />
                {` ${record.team.name}`}
              </>
            )}
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            {(record.status == '' || record.status == 'not submitted') && (
              <Tag color={'blue'}>Not submitted</Tag>
            )}
            {record.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
            {record.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
            {record.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
          </Space>
        ),
      },
      {
        title: 'Score',
        dataIndex: 'marks',
        key: 'marks',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            {!(record.status == '' || record.status == 'not submitted') && (
              <Badge
                count={
                  record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
                    record.criteriaSet.criterias.length +
                  '%'
                }
                showZero
                overflowCount={120}
                style={{ fontSize: '16px' }}
                color={
                  record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
                    record.criteriaSet.criterias.length >
                  85
                    ? '#52c41a'
                    : record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
                        record.criteriaSet.criterias.length >
                      60
                    ? '#faad14'
                    : ''
                }
              />
              // <Progress
              //   type="circle"
              //   percent={
              //     record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
              //     record.criteriaSet.criterias.length
              //   }
              //   width={65}
              //   format={() =>
              //     `${
              //       record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
              //       record.criteriaSet.criterias.length
              //     }%`
              //   }
              // />
            )}
          </Space>
        ),
      },
    ];
  }, []);

  useEffect(() => {
    setLoading(true);
    if (cycle) {
      connection
        .get(`goals/internal-customers/${params.id}/${cycle}`)
        .then((res: any) => {
          const data = res.reviews.map((item: any) => {
            return { ...item, teamName: item.team.name };
          });
          setTableData(data);
        })
        .finally(() => setLoading(false));
    }
  }, [connection, cycle, params.id]);

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title={`${decodeURIComponent(params?.name ?? '')} Internal Customer Reviews`}
        subTitle="View Internal Customer reviews"
        onBack={() => history1.goBack()}
        breadcrumb={{
          items: [
            {
              path: '',
              title: `Internal Customer Review/ ${decodeURIComponent(params?.name ?? '')}`,
            },
          ],
        }}
      />
      <WebUxPageContent>
        <WebUxPageItem>
          <FlexBox>
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => setCycle(value, 'replace')}
              data={cycles}
            />
          </FlexBox>
          <Spin spinning={loading}>
            <Table
              size="small"
              columns={columns}
              dataSource={tableData}
              expandable={{ indentSize: 0 }}
              pagination={false}
            />
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={modalVisible}
        onCancel={() => {
          setRecord(undefined);
          setModalVisible(false);
        }}
        okButtonProps={{
          hidden: true,
        }}>
        {record && (
          <>
            <Row style={{ marginTop: '3%' }}>
              <UserAvatar size={80} />
              <Col style={{ marginLeft: '3%', marginTop: '1%' }}>
                <Row style={{ alignItems: 'center' }}>
                  <Space>
                    <Title level={3} style={{ margin: 0 }}>
                      {record?.teamName}
                    </Title>
                    <div>
                      <Tag color={record?.teamType?.color}>{record?.teamType?.name}</Tag>
                    </div>
                  </Space>
                </Row>
                <Text type="secondary">{record?.teamDescription}</Text>
              </Col>
            </Row>
            <Divider style={{ marginTop: '2%' }} />
            <div style={{ marginLeft: '1%' }}>
              <ReviewPreview
                mode={'view'}
                criterias={record?.criteriaSet?.criterias.map((c: any) => ({
                  ...c,
                  answer: ((c?.answer ?? 0) / 100) * c.type ?? 5,
                }))}
              />
            </div>
            <Space style={{ marginTop: '2%' }}>
              <AlignLeftOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Overall Comment</Text>
            </Space>
            <Divider orientation="left" style={{ marginTop: 0, marginBottom: 0 }} />
            <div style={{ marginTop: '2%' }}>
              <Typography>
                {record?.overallComment && !!record?.overallComment?.replace(regex, '').length
                  ? ReactHtmlParser(record?.overallComment)
                  : ''}
              </Typography>
            </div>
            <Row style={{ alignItems: 'center', marginTop: 20 }}>
              <Space>
                <Title level={5}>Activity</Title>
                <RetweetOutlined style={{ fontSize: 20 }} />
              </Space>
            </Row>
            <Link onClick={() => setShowContent(!showContent)}>
              <Space className="div_hover">
                <Text>Show Activities</Text>
                {!showContent ? (
                  <CaretDownOutlined style={{ marginTop: 7, color: 'rgba(0,0,0,0.7)' }} />
                ) : (
                  <CaretUpOutlined style={{ marginTop: 7, color: 'rgba(0,0,0,0.7)' }} />
                )}
              </Space>
            </Link>
            {record?.internalCustomerReviewHistory?.length !== 0 &&
              showContent &&
              record?.internalCustomerReviewHistory?.map((item: any, key: any) => {
                const objectionStatusMeta = getObjectionStatusMeta(item.objectionStatus);

                return (
                  <div key={key} className="borderBox">
                    <Row>
                      <Space>
                        <UserAvatar src={item?.image} />
                        <Text strong>{item?.name}</Text>
                        {/*<Text type="secondary">{item.date + ' ' + item.time}</Text>*/}
                      </Space>
                    </Row>
                    <Row>
                      <Text strong>
                        Status:
                        {(record.status == '' || record.status == 'not submitted') && (
                          <Tag color={'blue'}>Not submitted</Tag>
                        )}
                        {item.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
                        {item.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
                        {item.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
                      </Text>
                    </Row>
                    {item?.status === 'recalled' && (
                      <div style={{ marginBottom: 10 }}>
                        <Row style={{ marginLeft: 20 }}>
                          <Text strong>Recall Reason</Text>
                        </Row>
                        <Row style={{ marginLeft: 20 }}>
                          <Text type="secondary">
                            {item.recallReason && !!item.recallReason?.replace(regex, '').length
                              ? ReactHtmlParser(item.recallReason)
                              : ''}
                          </Text>
                        </Row>
                      </div>
                    )}
                    {item?.objectionReason && (
                      <div style={{ marginBottom: 10 }}>
                        <Row style={{ marginLeft: 20 }}>
                          <Space>
                            <Text strong>Objection</Text>
                            {objectionStatusMeta && (
                              <Tag color={objectionStatusMeta.color}>
                                {objectionStatusMeta.label}
                              </Tag>
                            )}
                          </Space>
                        </Row>
                        <Row style={{ marginLeft: 20 }}>
                          <Text type="secondary">
                            {item.objectionReason &&
                            !!item.objectionReason?.replace(regex, '').length
                              ? ReactHtmlParser(item.objectionReason)
                              : ''}
                          </Text>
                        </Row>
                      </div>
                    )}
                    {item?.status === 'submitted' &&
                      item.history?.map((e: any, index1: any) => {
                        return (
                          <div key={index1}>
                            <Row style={{ marginTop: 20 }}>
                              <Text type="secondary">{`Submitted ${e.criteriaName}`}</Text>
                            </Row>
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Answer</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Rate
                                    value={(e.newAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                </Row>
                              </div>
                            )}
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                            {e.newAnswer === 0 && e.oldAnswer === 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {item?.status !== 'submitted' &&
                      item.history?.map((e: any, index1: any) => {
                        return (
                          <div key={index1}>
                            <Row style={{ marginTop: 10 }}>
                              <Text type="secondary">{`Updated ${e.criteriaName}`}</Text>
                            </Row>
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Answer</Text>
                                </Row>
                                <Row
                                  style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
                                  <Rate
                                    value={(e.oldAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                  <ArrowRightOutlined
                                    style={{ paddingTop: 6, paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Rate
                                    value={(e.newAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                </Row>
                              </div>
                            )}
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>

                                <Row
                                  style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
                                  <Text type="secondary">{`${e.oldComment}   `}</Text>
                                  <ArrowRightOutlined
                                    style={{ paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                            {e.newAnswer === 0 && e.oldAnswer === 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.oldComment && !!e.oldComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.oldComment)
                                      : ''}
                                  </Text>
                                  <ArrowRightOutlined
                                    style={{ paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </>
        )}
      </Modal>
    </WebUxPage>
  );
};

export default UserInternalCustomerAnswers;
